import { useMemo, useState } from 'react';

type UseBooleanStateProps = {
    /**
     * Current state value.
     */
    state: boolean;

    /**
     * Current state value setter.
     */
    setState: (state: boolean) => void;

    /**
     * `true` setter function.
     */
    setTrue: VoidFunction;

    /**
     * `false` setter function.
     */
    setFalse: VoidFunction;

    /**
     * Function to change the value to the opposite of the current one.
     */
    toggle: VoidFunction;
};

/**
 * Hook for tracking boolean using useState.
 */
export const useBooleanState = (initialState: boolean | (() => boolean)): UseBooleanStateProps => {
    const [state, setState] = useState<boolean>(initialState);

    const [setTrue, setFalse, toggle] = useMemo(
        () => [() => setState(true), () => setState(false), () => setState((prevState) => !prevState)],
        [],
    );

    return {
        state,
        setState,
        setTrue,
        setFalse,
        toggle,
    };
};
